<template>
	<div data-tour-scope="assistants-list">
		<v-main>
			<v-container fluid fill-height>
				<v-layout justify-center align-center>
					<v-card width="100%" outlined elevation-0>
						<v-toolbar flat class="border_bottom" shaped>
							<v-toolbar-title> {{ $vuetify.lang.t('$vuetify.front.ecommerce.assistant') }}</v-toolbar-title>
							<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()"><v-icon left>mdi-school-outline</v-icon>Tutorial guidato</v-btn>
							<v-spacer></v-spacer>
						</v-toolbar>


						<v-col cols="12">
							<v-row data-tour="filters">
								<v-col cols="12" md="5" data-tour="search">
									<v-text-field
										v-model="searchfield"
										label="Cerca nei messaggi"
										outlined
										dense
										hide-details
										clearable
										append-icon="mdi-magnify"
										@click:append="getSearch()"
										@keyup.enter="getSearch()"
										@click:clear="clearSearch()"
									></v-text-field>
								</v-col>
								<v-col cols="12" md="3">
									<v-select v-model="settings.filters.lang" :items="lingue" label="Lingua" multiple clearable outlined dense hide-details></v-select>
								</v-col>
								<v-col cols="12" md="4">
									<v-menu v-model="menuData" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
										<template v-slot:activator="{ on }">
											<v-text-field :value="dataLabel" v-on="on" label="Data ultimo messaggio" outlined dense hide-details clearable readonly @click:clear="settings.filters.lastmessage = []"></v-text-field>
										</template>
										<v-date-picker v-model="settings.filters.lastmessage" range @change="menuData = false"></v-date-picker>
									</v-menu>
								</v-col>
							</v-row>
						</v-col>

						<v-data-table
							:headers="headers"
							:items="getItems"
							:options.sync="settings.options"
							must-sort
							:server-items-length="total"
							class="elevation-0"
							:footer-props="{ 'items-per-page-options': listSize }"
							item-key="_id"
							:mobile-breakpoint="0"
							data-tour="table"
							no-data-text="Nessuna conversazione con questi filtri"
							:hide-default-header="mobileCards"
							:class="{ 'table-cards': mobileCards }"
						>
							<template v-slot:body="{ items }">
								<!-- telefono: ogni conversazione e' una card; il "+" apre la chat DENTRO la card -->
								<tbody v-if="mobileCards" class="assistant-cards">
									<tr v-for="item in items" :key="item._id">
										<td :colspan="headers.length" class="px-3 py-1">
											<key-value-card :title="domanda(item) || 'Conversazione'" :fields="campiCard(item)">
												<template v-slot:actions>
													<v-btn small icon color="primary" data-tour="expand" :title="aperta(item._id) ? 'Chiudi' : 'Mostra conversazione'" @click="apriChiudi(item._id)">
														<v-icon>{{ aperta(item._id) ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
													</v-btn>
												</template>
												<AssistantChat v-if="aperta(item._id)" :messages="item.messages" />
											</key-value-card>
										</td>
									</tr>
									<tr v-if="!items.length">
										<td :colspan="headers.length" class="text-center grey--text py-6">Nessuna conversazione</td>
									</tr>
								</tbody>
								<tbody v-else>
									<template v-for="item in items">
										<tr :key="item._id">
											<td class="pr-0 mr-0" style="width: 5px">
												<v-btn small icon data-tour="expand" :title="aperta(item._id) ? 'Chiudi' : 'Mostra conversazione'" @click="apriChiudi(item._id)">
													<v-icon>{{ aperta(item._id) ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
												</v-btn>
											</td>
											<td>{{ item.lastmessage | moment('DD/MM/YYYY HH:mm:ss') }}</td>
											<td>
												<div class="ai-question" :title="domanda(item)">{{ domanda(item) }}</div>
											</td>
											<td>{{ item.lang }}</td>
											<td>{{ item.messages.length }}</td>
										</tr>
										<tr v-if="aperta(item._id)" :key="item._id + '-chat'" class="ai-expanded">
											<td colspan="5" class="py-3">
												<AssistantChat :messages="item.messages" />
											</td>
										</tr>
									</template>
								</tbody>
							</template>
						</v-data-table>
					</v-card>
				</v-layout>
			</v-container>
		</v-main>

		<!-- Ultimo elemento: il nodo viene spostato in `[data-app]` al montaggio. -->
		<TourOverlay :tour="tour"></TourOverlay>
	</div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import KeyValueCard from '@/components/core/KeyValueCard.vue';
import AssistantChat from '@/components/assistant/AssistantChat.vue';
import { useTour } from '@/components/core/tour/useTour';
import { listRouteState } from '@/components/core/list/listRouteState';
import { createAssistantsTourSteps, stopAssistantsTour } from './assistants-tour';

const LIST_SIZE = [15, 25, 50, 100];

/*
 * Conversazioni dell'assistente AI del sito, in sola lettura: ogni riga e' una chat, il "+"
 * la apre sotto la riga. Ricerca nei messaggi, lingua e data dell'ultimo messaggio filtrano
 * lato server; pagina, righe, ordinamento e filtri stanno nell'URL.
 */
export default {
	name: 'AssistantsList',
	components: { TourOverlay, KeyValueCard, AssistantChat },
	mixins: [listRouteState({ filters: { lang: 'array', lastmessage: 'array' }, perPageOptions: LIST_SIZE })],
	setup() {
		return { tour: useTour(createAssistantsTourSteps(), { onStop: stopAssistantsTour }) };
	},
	data() {
		return {
			settings: this.listSettingsFromRoute({
				search: '',
				filters: { lang: [], lastmessage: [] },
				options: { sortBy: ['lastmessage'], sortDesc: [true], page: 1, itemsPerPage: 15 },
			}),
			listSize: LIST_SIZE,
			searchfield: '',
			total: 0,
			// conversazioni aperte sotto la riga
			aperte: [],
			menuData: false,
			headers: [
				{ text: '', value: '', sortable: false },
				{ text: 'Data', value: 'lastmessage' },
				{ text: 'Domanda', value: 'question', sortable: false },
				{ text: 'Lingua', value: 'lang', sortable: false },
				{ text: 'Messaggi', value: 'messages', sortable: false },
			],
		};
	},
	computed: {
		...mapGetters({
			getItems: 'assistant/getItems',
			getSetting: 'settings/getSetting',
		}),
		// sotto i 769px le righe diventano card
		mobileCards() {
			return this.$vuetify.breakpoint.width < 769;
		},
		// lingue attive del profilo, per il filtro Lingua
		lingue() {
			return ((this.getSetting && this.getSetting.languages) || []).filter(l => l.is_active !== false).map(l => l.lang);
		},
		dataLabel() {
			return (this.settings.filters.lastmessage || []).map(v => moment(v).format('DD/MM/YYYY')).join(' ~ ');
		},
	},
	mounted() {
		this.fetchSetting();
		// stato della lista letto dall'URL in data()
		this.listLoad();
	},
	beforeDestroy() {
		this.tour.stop();
		this.destroyData();
	},
	methods: {
		...mapActions({
			fetchItems: 'assistant/asyncFetchData',
			destroyData: 'assistant/destroyData',
			fetchSetting: 'settings/asyncFetchData',
		}),
		...mapMutations({
			setPreload: 'settings/setPreload',
		}),
		async getDataFromApi() {
			this.setPreload(true);
			try {
				const result = await this.fetchItems(this.settings);
				this.total = (result && result.total) || 0;
			} finally {
				this.setPreload(false);
			}
		},
		// ricerca da tre caratteri; il caricamento lo fa listRouteState
		getSearch() {
			this.settings.search = _.size(this.searchfield) >= 3 ? this.searchfield.trim() : '';
		},
		clearSearch() {
			this.searchfield = '';
			this.settings.search = '';
		},
		domanda(item) {
			return item.messages && item.messages[1] ? item.messages[1].content : '';
		},
		campiCard(item) {
			return [
				{ label: 'Data', value: item.lastmessage ? moment(item.lastmessage).format('DD/MM/YYYY HH:mm:ss') : '' },
				{ label: 'Lingua', value: item.lang },
				{ label: 'Messaggi', value: item.messages ? item.messages.length : 0, strong: true },
			];
		},
		aperta(id) {
			return this.aperte.includes(id);
		},
		apriChiudi(id) {
			const i = this.aperte.indexOf(id);
			if (i === -1) this.aperte.push(id);
			else this.aperte.splice(i, 1);
		},
	},
};
</script>

<style scoped>
/* vista card: niente layout a colonne, ogni riga e' un blocco e la card prende il 100% */
.table-cards ::v-deep table {
	display: block;
	width: 100%;
}
.assistant-cards,
.assistant-cards tr {
	display: block;
	width: 100%;
}
.assistant-cards tr,
.assistant-cards tr:hover {
	background: transparent !important;
}
.assistant-cards td {
	display: block;
	width: 100%;
	border-bottom: none !important;
	height: auto !important;
}
/* respiro tra le card e il bordo della card che contiene la tabella */
.assistant-cards tr:first-child td {
	padding-top: 12px !important;
}
.assistant-cards tr:last-child td {
	padding-bottom: 12px !important;
}
.assistant-cards tr + tr td {
	padding-top: 8px !important;
}
/* domanda del cliente: massimo 2 righe, la riga non si allunga */
.ai-question {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	max-width: 520px;
}
/* riga espansa della tabella desktop: niente hover grigio sulla chat */
.ai-expanded,
.ai-expanded:hover {
	background: transparent !important;
}
</style>
